import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import _, { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { toastError, toastSuccess, getOrganizationId } from "../../../../utils";
import {
  initialAddFleet,
  initialCreatePortOperation,
  isSingleOperationTaken,
  singleInstanceOperations,
  termsCoEfficientTable,
} from "../../../../constants";
import {
  addVesselVesselPortDistanceToOperations,
  convertPortOperationToDistanceString,
  fleetWithCalculatorDataMapping,
  calculationValidation,
  addPortDistanceToSelectedFleet,
} from "../dataMapping";

import {
  getSingleVesselVesselDetailsApi,
  updateVesselVesselDetailsApi,
  addCargoDetailsApi,
  deleteCargoExtraDetailVesselVesselApi,
  getPortDistanceVesselVesselByPortListApi,
  sendCalculatorDataApi,
  resetDistanceApi,
  savePortDistanceApi,
} from "../services/vesselVesselApi";

import { useVesselVesselStore } from "../state/useVesselVesselStore";

export function useVesselVesselController(props = {}) {
  const { setLoader = () => {}, setIsFullScreen = () => {} } = props;
  const { id = "" } = useParams();
  const location = useLocation();
  const history = useHistory();

  const store = useVesselVesselStore();
  const {
    calculatorData,
    selectedVessels,
    syncBunker,
    isOverride,
    portDistanceResponse,
    realPortDistanceResponse,
    portDistancePayloadResponse,
    setCalculatorData,
    setPortOperations,
    setFleetList,
    setSelectedQuery,
    setSelectedVessels,
    setSyncBunker,
    setIsOverride,
    setPortDistanceResponse,
    setRealPortDistanceResponse,
    setPortDistancePayloadResponse,
  } = store;

  const stateRef = useRef(store);
  useEffect(() => {
    stateRef.current = store;
  });

  const fetchPortDistance = useRef(true);
  const [hideOrder, setHideOrder] = useState(false);
  const [isPublic, setPublic] = useState(false);
  const [allQueryFormat, setAllQueryFormat] = useState([]);
  const [hideCalculator, setHideCalculator] = useState(false);
  const [initialFleetList, setInitialFleetList] = useState([]);
  const [addFleetsArray, setAddFleetsArray] = useState([{ ...initialAddFleet }]);
  const [selectedSplitHire, setSelectedSplitHire] = useState({});
  const [selectedSplitHireVesselOut, setSelectedSplitHireVesselOut] = useState({});

  // Modals
  const [marginModal, setMarginModal] = useState(false);
  const [supplyModal, setSupplyModal] = useState(false);
  const [addFleetModal, setAddFleetModal] = useState(false);
  const [splitHireModal, setSplitHireModal] = useState(false);
  const [splitHireModalVesselOut, setSplitHireModalVesselOut] = useState(false);
  const [openAddOperation, setOpenAddOperation] = useState(false);
  const [openAddOperationIndex, setOpenAddOperationindex] = useState(false);
  const [getCurrentResult, setCurrentResult] = useState({});

  const [fleetCheckbox, setFleetCheckbox] = useState({
    ballest: { 1: true, 2: true, 3: true, 4: true },
    laden: { 1: true, 2: true, 3: true, 4: true },
  });

  const onUpdateAndSavePortDistance = useCallback(
    ({ updateData = {}, saveData = {}, updatedCalculatorData = {} }) => {
      setLoader(true, "Saving port distances...");
      savePortDistanceApi(saveData)
        .then(() => {
          setLoader(false);
          sendCalculationRequest(updatedCalculatorData);
        })
        .catch((err) => {
          setLoader(false);
          toastError(err?.data?.message || "Something went wrong.");
        });
    },
    [setLoader]
  );

  const delayUpdateAndSaveOperation = useCallback(
    _.debounce((data) => onUpdateAndSavePortDistance(data), 500),
    [onUpdateAndSavePortDistance]
  );

  const onChangeOverride = useCallback((val) => {
    setIsOverride(val);
  }, [setIsOverride]);

  const onFetchAndSetIntitalPortDistance = useCallback(
    async (updatedCalculatorData = {}) => {
      const { getPortDistanceString, routeList } =
        convertPortOperationToDistanceString(updatedCalculatorData);

      const { clientQuery = {} } = updatedCalculatorData;
      const {
        eca = false,
        hra = false,
        jwc = false,
        suez = false,
        panama = false,
        singapore = false,
      } = clientQuery;

      const portDataObj = {
        eca,
        hra,
        jwc,
        suez,
        panama,
        singapore,
        routeOverride: false,
        getGeometry: true,
        routeList,
        portList: getPortDistanceString,
        clientQueryId: id,
        queryId: id,
        id,
      };

      try {
        const res = await getPortDistanceVesselVesselByPortListApi(portDataObj);
        fetchPortDistance.current = false;
        setPortDistanceResponse(res.data);
        setRealPortDistanceResponse(res?.realData);
        setPortDistancePayloadResponse(res?.payload);
        return res;
      } catch (err) {
        setLoader(false);
        toastError(err?.data?.message);
        return { error: true };
      }
    },
    [id, setLoader, setPortDistanceResponse, setRealPortDistanceResponse, setPortDistancePayloadResponse]
  );

  const sendCalculatorResponse = useCallback((modifiedData = {}) => {
    return new Promise((resolve) => {
      if (!modifiedData.fleetList || modifiedData.fleetList.length === 0) return resolve({});

      const updatedCargoList = modifiedData.cargoList.map((cargoDetails) => {
        const { _id, ...restCargoMainDetails } = cargoDetails;
        const { _id: cargoDetailId, ...restCargoDetail } = cargoDetails?.cargoDetails || {};
        return {
          ...restCargoMainDetails,
          cargoDetails: { ...restCargoDetail },
        };
      });

      const {
        _id,
        cargoList,
        createdUser,
        lastModifiedUser,
        userId,
        calculationFormat,
        __v,
        createdAt,
        updatedAt,
        isCalculate,
        ...restClientQuery
      } = modifiedData.clientQuery || {};

      const updatedClientQuery = {
        ...restClientQuery,
        cargoList: null,
      };

      const updatedFleetListData =
        modifiedData?.fleetList?.length > 0
          ? modifiedData.fleetList.map((fleetDetails) => {
              const vesselDetails = !!fleetDetails?.vessel
                ? JSON.parse(fleetDetails.vessel)
                : {};
              const {
                _id: parseVesselId,
                userId: parseVesselUserId,
                createdUser: parseVesselCreatedUser,
                lastModifiedUser: parseVesselModifiedUser,
                vesselExtraDetails,
                __v: parseVessel__v,
                createdAt,
                createdDate,
                lastModifiedDate,
                pattern,
                updatedAt,
                vesselLabel,
                vesselAHL,
                vesselCO2,
                vesselCountry,
                vesselGrabs,
                vesselIceClass,
                vesselIMO,
                vesselLdnConsmMGO1,
                vesselLdnConsmMGO2,
                vesselLightShip,
                vesselMMSI,
                vesselType,
                vesselSource,
                vesselName,
                ...parseVesselDetails
              } = vesselDetails;
              const {
                _id,
                vesselId,
                createdUser,
                organizationId,
                userId,
                lastModifiedUser,
                vesselUniqueId,
                __v,
                ...rest
              } = fleetDetails;
              const uniqueId = vesselUniqueId;

              return {
                ...rest,
                vessel: JSON.stringify({
                  id: uniqueId,
                  ...parseVesselDetails,
                  system: true,
                }),
                vesselId: uniqueId,
              };
            })
          : [];

      const updatedCargoExtraDetails = (modifiedData.cargoExtraDetails || []).map(
        (cargoextraDetails, i) => {
          const { _id, ...restCargoMainDetails } = cargoextraDetails;
          const { _id: cargoDetailId, ...restCargoDetail } =
            cargoextraDetails.cargoDetails || {};
          return {
            ...restCargoMainDetails,
            index: i + 1,
            cargoDetails: {
              ...restCargoDetail,
            },
          };
        }
      );

      const calculationData = {
        autoSave: false,
        cargoExtraDetails: updatedCargoExtraDetails,
        cargoList: updatedCargoList,
        clientQuery: updatedClientQuery,
        fleetList: updatedFleetListData,
      };

      sendCalculatorDataApi(calculationData)
        .then((res) => resolve(res))
        .catch((err) => {
          resolve({});
          toastError(err?.data?.message || "Something went wrong");
        });
    });
  }, []);

  const saveAllResultUpdatedData = useCallback(
    (updatedData) => {
      const { disableRefetch = false, ...restClientQuery } = updatedData.clientQuery || {};
      updateVesselVesselDetailsApi(id, {
        ...updatedData,
        clientQuery: restClientQuery,
      })
        .then((res) => {
          if (res) {
            toastSuccess("Calculation updated and saved successfully!");
          }
        })
        .catch((err) => {
          setLoader(false);
          toastError(err?.data?.message);
        });
    },
    [id, setLoader]
  );

  const VesselOutFuelCalculate = useCallback((updatedCalculatorData, fleet) => {
    const portOperation = updatedCalculatorData?.portOperation || [];
    const fleetDetails = fleet || {};

    let bunkerSuppliedHSFOTotal = 0;
    let bunkerSuppliedLSFOTotal = 0;
    let bunkerSuppliedMGOTotal = 0;

    let bunkerSuppliedHSFOSupplyInternalTotal = 0;
    let bunkerSuppliedLSFOSupplyInternalTotal = 0;
    let bunkerSuppliedMGOSupplyInternalTotal = 0;

    let hsReserveInternalTotal = 0;
    let lsReserveInternalTotal = 0;
    let mgoReserveInternalTotal = 0;

    let hsFuelPriceReserveInternalTotal = 0;
    let lsFuelPriceReserveInternalTotal = 0;
    let mgoFuelPriceReserveInternalTotal = 0;

    const { fuelType = "" } = updatedCalculatorData?.clientQuery || {};
    const currentType = fuelType.replace("TYPE", "");

    let hsFuelPriceTotal = 0;
    let lsFuelPriceTotal = 0;
    let mgoFuelPriceTotal = 0;

    let hsFuelPriceSupplyInternalTotal = 0;
    let lsFuelPriceSupplyInternalTotal = 0;
    let mgoFuelPriceSupplyInternalTotal = 0;

    let totalPortExpense = 0;
    for (let i = 0; i < portOperation?.length; i++) {
      const cargoDetails = portOperation[i]?.cargoDetails || {};
      bunkerSuppliedHSFOTotal += Number(cargoDetails.bunkerSuppliedHSFO || 0);
      bunkerSuppliedLSFOTotal += Number(cargoDetails.bunkerSuppliedLSFO || 0);
      bunkerSuppliedMGOTotal += Number(cargoDetails.bunkerSuppliedMGO || 0);

      const isOwnerSupply = cargoDetails.paidBy === "Owner" && cargoDetails.reserve === "Supply";
      bunkerSuppliedHSFOSupplyInternalTotal += isOwnerSupply ? Number(cargoDetails.bunkerSuppliedHSFO || 0) : 0;
      bunkerSuppliedLSFOSupplyInternalTotal += isOwnerSupply ? Number(cargoDetails.bunkerSuppliedLSFO || 0) : 0;
      bunkerSuppliedMGOSupplyInternalTotal += isOwnerSupply ? Number(cargoDetails.bunkerSuppliedMGO || 0) : 0;

      const isOwnerReserve = cargoDetails.paidBy === "Owner" && cargoDetails.reserve === "Reserve";
      hsReserveInternalTotal += isOwnerReserve ? Number(cargoDetails.bunkerSuppliedHSFO || 0) : 0;
      lsReserveInternalTotal += isOwnerReserve ? Number(cargoDetails.bunkerSuppliedLSFO || 0) : 0;
      mgoReserveInternalTotal += isOwnerReserve ? Number(cargoDetails.bunkerSuppliedMGO || 0) : 0;

      const currentHsfo = cargoDetails[`fuelPriceType${currentType}Hsfo`] || 0;
      const currentLsfo = cargoDetails[`fuelPriceType${currentType}Lsfo`] || 0;
      const currentMgo = cargoDetails[`fuelPriceType${currentType}Mgo`] || 0;

      hsFuelPriceTotal += Number(cargoDetails.bunkerSuppliedHSFO || 0) * Number(currentHsfo);
      lsFuelPriceTotal += Number(cargoDetails.bunkerSuppliedLSFO || 0) * Number(currentLsfo);
      mgoFuelPriceTotal += Number(cargoDetails.bunkerSuppliedMGO || 0) * Number(currentMgo);

      hsFuelPriceSupplyInternalTotal += isOwnerSupply ? Number(cargoDetails.bunkerSuppliedHSFO || 0) * Number(currentHsfo) : 0;
      lsFuelPriceSupplyInternalTotal += isOwnerSupply ? Number(cargoDetails.bunkerSuppliedLSFO || 0) * Number(currentLsfo) : 0;
      mgoFuelPriceSupplyInternalTotal += isOwnerSupply ? Number(cargoDetails.bunkerSuppliedMGO || 0) * Number(currentMgo) : 0;

      hsFuelPriceReserveInternalTotal += isOwnerReserve ? Number(cargoDetails.bunkerSuppliedHSFO || 0) * Number(currentHsfo) : 0;
      lsFuelPriceReserveInternalTotal += isOwnerReserve ? Number(cargoDetails.bunkerSuppliedLSFO || 0) * Number(currentLsfo) : 0;
      mgoFuelPriceReserveInternalTotal += isOwnerReserve ? Number(cargoDetails.bunkerSuppliedMGO || 0) * Number(currentMgo) : 0;

      totalPortExpense += Number(portOperation[i]?.portExp || 0);
    }

    const voyConsumptionFuelQty = {
      hsQuantity: fleetDetails?.hsQuantity || 0,
      lsQuantity: fleetDetails?.lsQuantity || 0,
      mgoQuantity: fleetDetails?.mgoQuantity || 0,
    };
    const supplyBalanceFuelQty = {
      hsSupplyBalance: bunkerSuppliedHSFOSupplyInternalTotal + Number(fleetDetails?.hsQuantity || 0),
      lsSupplyBalance: bunkerSuppliedLSFOSupplyInternalTotal + Number(fleetDetails?.lsQuantity || 0),
      mgoSupplyBalance: bunkerSuppliedMGOSupplyInternalTotal + Number(fleetDetails?.mgoQuantity || 0),
    };
    const balanceFuelQty = {
      hsBalance: supplyBalanceFuelQty?.hsSupplyBalance + hsReserveInternalTotal,
      lsBalance: supplyBalanceFuelQty?.lsSupplyBalance + lsReserveInternalTotal,
      mgoBalance: supplyBalanceFuelQty?.mgoSupplyBalance + mgoReserveInternalTotal,
    };

    const voyConsumptionFuelPrice = {
      hsFuelPrice: Number(fleetDetails?.fuelHsExpense || 0) / (voyConsumptionFuelQty?.hsQuantity || 1),
      lsFuelPrice: Number(fleetDetails?.fuelLsExpense || 0) / (voyConsumptionFuelQty?.lsQuantity || 1),
      mgoFuelPrice: Number(fleetDetails?.fuelMgoExpense || 0) / (voyConsumptionFuelQty?.mgoQuantity || 1),
    };
    const supplyBalanceFuelCost = {
      hsSupplyBalanceFuelCost: hsFuelPriceSupplyInternalTotal - parseInt(fleetDetails?.fuelHsExpense || 0),
      lsSupplyBalanceFuelCost: lsFuelPriceSupplyInternalTotal - parseInt(fleetDetails?.fuelLsExpense || 0),
      mgoSupplyBalanceFuelCost: mgoFuelPriceSupplyInternalTotal - parseInt(fleetDetails?.fuelMgoExpense || 0),
    };

    const balanceFuelCost = {
      hsBalanceFuelCost: supplyBalanceFuelCost?.hsSupplyBalanceFuelCost + hsFuelPriceReserveInternalTotal,
      lsBalanceFuelCost: supplyBalanceFuelCost?.lsSupplyBalanceFuelCost + lsFuelPriceReserveInternalTotal,
      mgoBalanceFuelCost: supplyBalanceFuelCost?.mgoSupplyBalanceFuelCost + mgoFuelPriceReserveInternalTotal,
    };

    const fuelPrice = {
      hsFuelPrice: balanceFuelCost?.hsBalanceFuelCost / (balanceFuelQty?.hsBalance || 1),
      lsFuelPrice: balanceFuelCost?.lsBalanceFuelCost / (balanceFuelQty?.lsBalance || 1),
      mgoFuelPrice: balanceFuelCost?.mgoBalanceFuelCost / (balanceFuelQty?.mgoBalance || 1),
    };

    const deliveryOutFuelCost = {
      hsDeliveryOutFuelCost: balanceFuelQty?.hsBalance * Number(fleetDetails?.vesselOut?.bnkPriceDeliveryhs || 0),
      lsDeliveryOutFuelCost: balanceFuelQty?.lsBalance * Number(fleetDetails?.vesselOut?.bnkPriceDeliveryls || 0),
      mgoDeliveryOutFuelCost: balanceFuelQty?.mgoBalance * Number(fleetDetails?.vesselOut?.bnkPriceDeliverymgo || 0),
    };

    const fuelHandOverProfit = {
      hsFuelHandOverProfit: deliveryOutFuelCost?.hsDeliveryOutFuelCost - balanceFuelCost?.hsBalanceFuelCost,
      lsFuelHandOverProfit: deliveryOutFuelCost?.lsDeliveryOutFuelCost - balanceFuelCost?.lsBalanceFuelCost,
      mgoFuelHandOverProfit: deliveryOutFuelCost?.mgoDeliveryOutFuelCost - balanceFuelCost?.mgoBalanceFuelCost,
    };
    const TotalFuelHandOverProfit =
      fuelHandOverProfit?.hsFuelHandOverProfit +
      fuelHandOverProfit?.lsFuelHandOverProfit +
      fuelHandOverProfit?.mgoFuelHandOverProfit;

    const totalDuration = fleetDetails?.vesselOut?.DurationOut || 0;
    const splitHire = fleetDetails?.vesselOut?.splitHire
      ? JSON.parse(fleetDetails?.vesselOut?.splitHire)
      : [];

    let remainingDays = totalDuration;
    let splitHireAmount = 0;

    splitHire.forEach((item) => {
      if (remainingDays <= 0) return;
      const daysToApply = Math.min(remainingDays, item.NoDays);
      splitHireAmount += daysToApply * item.hireRate;
      remainingDays -= daysToApply;
    });

    const balanceDaysAfterSplit = remainingDays;
    const mainHireAmount = balanceDaysAfterSplit * Number(fleetDetails?.vesselOut?.hirePerDay || 0);

    const TCOGrossHire =
      (splitHireAmount ? splitHireAmount : 0) +
      (mainHireAmount ? mainHireAmount : 0) +
      (fleetDetails?.vesselOut?.hireGbb ? Number(fleetDetails?.vesselOut?.hireGbb) : 0);
    const TCOHireCommission = (TCOGrossHire * Number(fleetDetails?.vesselOut?.tcEq || 1)) / 100;
    const miscRevenue = fleetDetails?.vesselOut?.miscRevenue ? Number(fleetDetails?.vesselOut?.miscRevenue) : 0;
    const FuelSettlementProfit = fleetDetails?.fuelSettlementProfit ? Number(fleetDetails?.fuelSettlementProfit) : 0;
    const FuelHandOverProfit = TotalFuelHandOverProfit;
    const totalRevenue = TCOGrossHire + TCOHireCommission + miscRevenue + FuelSettlementProfit + FuelHandOverProfit;

    const fuelExpenses = fleetDetails?.fuelExpense ? Number(fleetDetails?.fuelExpense) : 0;
    const hireExpenses = fleetDetails?.hireExpense ? Number(fleetDetails?.hireExpense) : 0;
    const portExpenses = Number(totalPortExpense);
    const others = fleetDetails?.otherExpense ? Number(fleetDetails?.otherExpense) : 0;
    const totalExpenses = fuelExpenses + hireExpenses + portExpenses + others;

    const totalDur = Number(fleetDetails?.vesselOut?.DurationOut || 0) + Number(fleetDetails?.voyageDuration || 0);
    const profit = totalRevenue - (totalExpenses < 0 ? totalExpenses * -1 : totalExpenses);
    const profitPerDay = profit / ((totalDur < 0 ? totalDur * -1 : totalDur) || 1);

    return {
      bunkerSuppliedTotal: { bunkerSuppliedHSFOTotal, bunkerSuppliedLSFOTotal, bunkerSuppliedMGOTotal },
      bunkerSuppliedInternalTotal: { bunkerSuppliedHSFOSupplyInternalTotal, bunkerSuppliedLSFOSupplyInternalTotal, bunkerSuppliedMGOSupplyInternalTotal },
      reserveInternalTotal: { hsReserveInternalTotal, lsReserveInternalTotal, mgoReserveInternalTotal },
      voyConsumptionFuelQty,
      supplyBalanceFuelQty,
      balanceFuelQty,
      voyConsumptionFuelPrice,
      supplyBalanceFuelCost,
      balanceFuelCost,
      fuelPrice,
      deliveryOutFuelCost,
      fuelHandOverProfit,
      TotalFuelHandOverProfit,
      TCOGrossHire,
      TCOHireCommission,
      miscRevenue,
      FuelSettlementProfit,
      FuelHandOverProfit,
      totalRevenue,
      fuelExpenses,
      others,
      portExpenses,
      hireExpenses,
      totalExpenses,
      profit,
      profitPerDay,
      voyDuration: totalDur,
      hsQty: fleetDetails?.hsQuantity,
      lsQty: fleetDetails?.lsQuantity,
      mgoQty: fleetDetails?.mgoQuantity,
    };
  }, []);

  const onChangeFleetRowSelect = useCallback(
    async (
      fleet = {},
      resultCalculatorData = null,
      changeVessel = false,
      isPortDistanceChanged = false,
      isFromCalculateButton = false
    ) => {
      const currentState = stateRef.current;
      let updatedCalculatorData = resultCalculatorData || { ...currentState.calculatorData };
      let commencePort = (fleet && fleet.port) || "";
      let fleetFuelType = (fleet && fleet.vessel && JSON.parse(fleet.vessel)) || {};
      fleetFuelType = fleetFuelType.fuelType || "TYPE0";
      let portOperation = [...updatedCalculatorData.portOperation];

      const portToPortOperationResultMap = (fleet && fleet.portToPortOperationResultMap) || [];
      portOperation = portOperation.map((operation, index) => {
        const selectedResultMap = portToPortOperationResultMap[index] || {};
        const removeDistanceFromResultMap = ({ distanceECA = "", distanceNormal = "", ...rest }) => ({ ...rest });
        if (index !== 0) commencePort = (operation && operation.port) || "";
        let isTermExists = termsCoEfficientTable[0];
        if (operation.terms) {
          isTermExists = termsCoEfficientTable.find((item) => item.label === operation.terms);
        }
        let cargoDetails = { ...operation.cargoDetails };

        if (index === 0) {
          const currentType = fleetFuelType.replace("TYPE", "");
          const {
            bnkDelhs = 0,
            bnkDells = 0,
            bnkDelmgo = 0,
            bnkPriceDeliveryhs = 0,
            bnkPriceDeliveryls = 0,
            bnkPriceDeliverymgo = 0,
          } = fleet;
          cargoDetails = {
            ...cargoDetails,
            bunkerSuppliedHSFO: bnkDelhs,
            bunkerSuppliedLSFO: bnkDells,
            bunkerSuppliedMGO: bnkDelmgo,
            [`fuelPriceType${currentType}Hsfo`]: bnkPriceDeliveryhs,
            [`fuelPriceType${currentType}Lsfo`]: bnkPriceDeliveryls,
            [`fuelPriceType${currentType}Mgo`]: bnkPriceDeliverymgo,
          };
        }
        return {
          ...removeDistanceFromResultMap(selectedResultMap),
          ...operation,
          cargoDetails,
          port: commencePort,
          terms: isTermExists ? isTermExists.label : operation.terms,
          factor: isTermExists ? isTermExists.value : 0,
        };
      });

      if (!isPortDistanceChanged) {
        let portDistanceRes = currentState.portDistanceResponse;
        if (fetchPortDistance.current) {
          const resObj = await onFetchAndSetIntitalPortDistance(updatedCalculatorData);
          if (resObj && resObj.error) return;
          portDistanceRes = resObj?.data;
        }
        portOperation = addVesselVesselPortDistanceToOperations(
          portDistanceRes,
          [...portOperation],
          currentState.realPortDistanceResponse,
          true
        );
      }

      fleet = addPortDistanceToSelectedFleet(portOperation, fleet);
      const FuelHandOverProfitCount = VesselOutFuelCalculate(updatedCalculatorData, fleet);
      fleet.FuelHandOverProfitCount = FuelHandOverProfitCount;

      if (changeVessel || !currentState.selectedVessels._id) {
        setSelectedVessels(fleet);
      }

      const updatedFleetList = updatedCalculatorData.fleetList.map((singleFleet) => {
        if (singleFleet._id === fleet._id) return fleet;
        return singleFleet;
      });

      if (isFromCalculateButton) {
        setCurrentResult({
          ...updatedCalculatorData,
          clientQuery: {
            ...updatedCalculatorData.clientQuery,
            fuelType: fleetFuelType,
          },
          fleetList: updatedFleetList,
          portOperation,
        });
      }

      setCalculatorData({
        ...updatedCalculatorData,
        clientQuery: {
          ...updatedCalculatorData.clientQuery,
          fuelType: fleetFuelType,
        },
        fleetList: updatedFleetList,
        portOperation,
      });
    },
    [onFetchAndSetIntitalPortDistance, setSelectedVessels, setCalculatorData, VesselOutFuelCalculate]
  );

  const sendCalculationRequest = useCallback(
    async (sendData) => {
      const currentState = stateRef.current;
      let portDistanceRes = sendData?.portDistanceResponse || currentState.portDistanceResponse;
      let realData = sendData?.clientQuery?.portToPortDistanceReal?.output || currentState.realPortDistanceResponse;
      let payload = sendData?.clientQuery?.portToPortDistanceReal?.payload || currentState.portDistancePayloadResponse;

      if (fetchPortDistance.current) {
        const resObj = await onFetchAndSetIntitalPortDistance(sendData);
        if (resObj && resObj.error) return;
        portDistanceRes = resObj?.data;
        realData = resObj?.realData;
        payload = resObj?.payload;
      }



      sendData = {
        ...sendData,
        clientQuery: {
          ...sendData.clientQuery,
          portToPortDistanceReal: { output: realData, payload },
          portToPortDistance: { output: portDistanceRes, payload },
        },
        portOperation: addVesselVesselPortDistanceToOperations(
          portDistanceRes,
          [...sendData.portOperation],
          realData,
          true
        ),
      };

      const updatedFleedIds = sendData.fleetList.map((fleetDetails) => ({
        ...fleetDetails,
        vesselUniqueId: uuidv4(),
      }));

      const cargoList = [];
      const cargoExtraDetails = [];

      (sendData.portOperation || []).forEach((item) => {
        const op = String(item.operation || "").toLowerCase();
        const isCoreOperation =
          op.includes("commence") ||
          op.includes("loading") ||
          op.includes("discharging") ||
          op.includes("load") ||
          op.includes("disch") ||
          !item.isExtra;

        const sanitizedItem = {
          ...item,
          loadline: item.loadline || "S-Summer",
          sulfur: item.sulfur || "Normal",
        };

        if (isCoreOperation) {
          cargoList.push({
            ...sanitizedItem,
            isExtra: false,
          });
        } else {
          cargoExtraDetails.push({
            ...sanitizedItem,
            isExtra: true,
          });
        }
      });

      let modifiedData = {
        cargoList: cargoList.length > 0 ? cargoList : sendData.portOperation.map((item) => ({ ...item, isExtra: false })),
        cargoExtraDetails: cargoList.length > 0 ? cargoExtraDetails : [],
        fleetList: cloneDeep(updatedFleedIds),
        clientQuery: {
          ...sendData.clientQuery,
          calculationFormat: sendData.portOperation.map((item) => item._id).join(","),
        },
        autoSave: false,
      };

      await calculationValidation(modifiedData, currentState.selectedVessels);
      setLoader(true, "Calculating pricing and results...");
      const res = await sendCalculatorResponse(modifiedData);
      setLoader(false);

      const fleetData = fleetWithCalculatorDataMapping(cloneDeep(updatedFleedIds), res?.data);
      let fleetSelected = {};
      if (currentState.selectedVessels && currentState.selectedVessels._id) {
        fleetSelected = fleetData.find((item) => item._id === currentState.selectedVessels._id) || {};
      } else {
        fleetSelected = fleetData[0] || {};
      }

      onChangeFleetRowSelect(
        fleetSelected,
        { ...sendData, fleetList: fleetData },
        true,
        true,
        true
      );

      const newUpdatedQuery = {
        ...sendData.clientQuery,
        cargoList: sendData.portOperation,
        createdUser: sendData.clientQuery?.createdUser?._id,
        disableRefetch: sendData?.disableRefetch || false,
      };

      if (sendData.autoSave) {
        saveAllResultUpdatedData({
          fleetList: [fleetSelected],
          clientQuery: newUpdatedQuery,
        });
      }
    },
    [id, setLoader, onFetchAndSetIntitalPortDistance, sendCalculatorResponse, onChangeFleetRowSelect, saveAllResultUpdatedData]
  );

  const setInitialFetchedData = useCallback(
    async (mainRes1, initial = false) => {
      fetchPortDistance.current = true;
      const selectedQuery = mainRes1?.clientQuery || {};
      setAllQueryFormat(selectedQuery);
      setSelectedQuery(selectedQuery);

      if (mainRes1?._id) {
        setLoader(true, "Formatting query data...");
        let combinedCargo = mainRes1?.clientQuery?.cargoList;
        if (!combinedCargo || combinedCargo.length === 0) {
          combinedCargo = mainRes1?.cargoList || mainRes1?.cargoExtraDetails || [];
        }
        let formattedPortOperations = combinedCargo.map((portElement, i) => ({
          ...portElement,
          index: i,
        }));

        if (!selectedQuery.fuelCalculationMethod) selectedQuery.fuelCalculationMethod = "DEFAULT";
        if (!selectedQuery.fuelType) selectedQuery.fuelType = "TYPE0";

        if (["LIFO", "FIFO", "AVERAGE"].includes(selectedQuery.fuelCalculationMethod)) {
          setSyncBunker(true);
        }

        const currentState = stateRef.current;
        const updatedData = {
          ...currentState.calculatorData,
          clientQuery: selectedQuery,
          cargoList: selectedQuery.cargoList,
          portOperation: formattedPortOperations,
        };
        if (initial) updatedData.fleetList = cloneDeep(mainRes1?.fleetList);

        sendCalculationRequest(updatedData);
        setCalculatorData(updatedData);
      } else {
        setLoader(false);
      }
    },
    [setLoader, setSelectedQuery, setSyncBunker, sendCalculationRequest, setCalculatorData]
  );

  const getInitalQueryAndFleetData = useCallback(
    (targetId) => {
      setLoader(true, "Fetching query and fleet initial data...");
      getSingleVesselVesselDetailsApi(targetId)
        .then((res) => {
          const response1 = res?.data;
          setPublic(response1?.access === "public");
          setInitialFleetList(response1?.fleetList || []);
          setInitialFetchedData(response1, true);
        })
        .catch((err) => {
          setLoader(false);
          toastError(err?.data?.message);
        });
    },
    [setLoader, setInitialFetchedData]
  );

  const onChangePortOperations = useCallback(
    (index, e, cargoUpdate = false, isDate = false, datename) => {
      let value = isDate ? e : e.target?.value;
      let name = isDate ? datename : e.target?.name;

      const currentState = stateRef.current;
      let portOperation = [...currentState.calculatorData.portOperation];

      if (
        name === "operation" &&
        (singleInstanceOperations.includes(portOperation[index]?.operation) ||
          isSingleOperationTaken(portOperation, value))
      ) {
        return toastError("Delivery-out and Redelivery-in cannot be edited or duplicated.");
      }

      if (name === "terms") {
        const termFactor = termsCoEfficientTable.find((item) => item.label === value);
        portOperation[index]["terms"] = value;
        portOperation[index]["factor"] = (termFactor && termFactor.value) || 0;
      } else if (cargoUpdate) {
        if (!portOperation[index].cargoDetails) {
          portOperation[index].cargoDetails = {};
        }
        portOperation[index].cargoDetails[name] = value;
      } else {
        portOperation[index][name] = value;
      }

      let updatedCalculatorData = { ...currentState.calculatorData, portOperation };
      if (
        !cargoUpdate &&
        updatedCalculatorData?.clientQuery?.portToPortDistance?.output &&
        portOperation[index - 1] &&
        portOperation[index]?.port
      ) {
        const key = `${portOperation[index - 1]?.port}_${portOperation[index].port}`;
        if (updatedCalculatorData.clientQuery.portToPortDistance.output[key]) {
          updatedCalculatorData.clientQuery.portToPortDistance.output[key][name] = value;
        }
      }

      setCalculatorData(updatedCalculatorData);
    },
    [setCalculatorData]
  );

  const onChangePortCanalInQueryState = useCallback(
    (portCanalChecks) => {
      const currentState = stateRef.current;
      const updatedQuery = {
        ...currentState.calculatorData.clientQuery,
        ...portCanalChecks,
      };
      setAllQueryFormat([updatedQuery]);
      setSelectedQuery(updatedQuery);
    },
    [setSelectedQuery]
  );

  const onFetchPortOperationDistance = useCallback(
    (reset = false, propCalculator = null, portCanalChecks = {}, isSave = false) => {
      const currentState = stateRef.current;
      let updatedCalculatorData = propCalculator ? { ...propCalculator } : { ...currentState.calculatorData };

      const { getPortDistanceString, routeList } = convertPortOperationToDistanceString(updatedCalculatorData);
      const { clientQuery = {} } = updatedCalculatorData;
      const {
        eca = false,
        hra = false,
        jwc = false,
        suez = false,
        panama = false,
        singapore = false,
      } = clientQuery;

      const portDataObj = {
        eca,
        hra,
        jwc,
        suez,
        panama,
        singapore,
        routeOverride: false,
        getGeometry: true,
        ...portCanalChecks,
        routeList,
        portList: getPortDistanceString,
        clientQueryId: id,
        queryId: id,
        id,
      };

      onChangePortCanalInQueryState(portCanalChecks);
      const request = reset ? resetDistanceApi(portDataObj) : getPortDistanceVesselVesselByPortListApi(portDataObj);

      setLoader(true, "Recalculating distances...");
      request
        .then((res) => {
          setLoader(false);
          setPortDistanceResponse(res.data);
          setRealPortDistanceResponse(res?.realData);
          setPortDistancePayloadResponse(res?.payload);

          if (reset) {
            updatedCalculatorData = {
              ...updatedCalculatorData,
              clientQuery: {
                ...updatedCalculatorData.clientQuery,
                portToPortDistance: {
                  ...updatedCalculatorData.clientQuery.portToPortDistance,
                  output: res.data,
                },
              },
            };
          }

          let realData = reset ? JSON.parse(JSON.stringify(res.data)) : res.realData;
          const newPortOperations = addVesselVesselPortDistanceToOperations(
            res.data,
            [...updatedCalculatorData.portOperation],
            realData,
            true,
            reset
          );

          sendCalculationRequest({
            ...updatedCalculatorData,
            clientQuery: {
              ...updatedCalculatorData.clientQuery,
              portToPortDistanceReal: { payload: res.payload, output: realData },
              portToPortDistance: { payload: res.payload, output: res.data },
            },
            portOperation: newPortOperations,
            portDistanceResponse: res.data,
            autoSave: false,
            disableRefetch: true,
          });
        })
        .catch((err) => {
          setLoader(false);
          toastError(err?.data?.message || err?.message);
        });
    },
    [id, setLoader, onChangePortCanalInQueryState, setPortDistanceResponse, setRealPortDistanceResponse, setPortDistancePayloadResponse, sendCalculationRequest]
  );

  const onClickCalculate = useCallback(() => {
    sendCalculationRequest({ ...stateRef.current.calculatorData, autoSave: false });
  }, [sendCalculationRequest]);

  const onClickCalculateAndSave = useCallback(async () => {
    await sendCalculationRequest({ ...stateRef.current.calculatorData, autoSave: true });
  }, [sendCalculationRequest]);

  const calculationErrors = useMemo(() => {
    let errors = [];
    if (
      selectedVessels &&
      calculatorData.portOperation?.length &&
      calculatorData.fleetList?.length
    ) {
      errors = calculationValidation(calculatorData, selectedVessels);
    }
    return errors;
  }, [calculatorData, selectedVessels]);

  const onAddOperations = useCallback(
    (operations = {}, index) => {
      setLoader(true, "Adding port operation...");
      const currentState = stateRef.current;
      const targetIndex = (index !== undefined && index !== null && !isNaN(index) && index !== false)
        ? Number(index)
        : currentState.calculatorData.portOperation.length;

      addCargoDetailsApi({ details: operations, addIndex: targetIndex, id })
        .then((res) => {
          setLoader(false);
          toastSuccess("Port added successfully!");
          fetchPortDistance.current = true;
          let portOperation = [...currentState.calculatorData.portOperation];
          // Pick the new row by id: the server's stored order can differ from the on-screen order
          const currentIds = new Set(portOperation.map((op) => String(op._id)));
          const addedCargo =
            res?.data?.clientQuery?.cargoList?.find((item) => !currentIds.has(String(item._id))) ||
            res?.data ||
            operations;
          portOperation.splice(targetIndex, 0, {
            ...initialCreatePortOperation,
            ...addedCargo,
            loadline: addedCargo.loadline || initialCreatePortOperation.loadline || "S-Summer",
            sulfur: addedCargo.sulfur || initialCreatePortOperation.sulfur || "Normal",
            index: targetIndex,
          });
          portOperation = portOperation.map((item, i) => ({ ...item, index: i }));

          const updatedCalculatorData = {
            ...currentState.calculatorData,
            portOperation,
          };
          setCalculatorData(updatedCalculatorData);
          onFetchPortOperationDistance(false, updatedCalculatorData, {}, true);
        })
        .catch((err) => {
          setLoader(false);
          toastError(err?.data?.message || "Failed to add port.");
        });
    },
    [id, setLoader, setCalculatorData, onFetchPortOperationDistance]
  );

  const onDeleteOperations = useCallback(
    (index, rawCargoId) => {
      setLoader(true, "Deleting port operation...");
      const currentState = stateRef.current;
      const portOperationItem = currentState.calculatorData.portOperation?.[index];

      const cargoId =
        rawCargoId && rawCargoId !== "null" && rawCargoId !== "undefined"
          ? rawCargoId
          : portOperationItem?._id ||
            portOperationItem?.id ||
            portOperationItem?.cargoId ||
            portOperationItem?.cargoDetails?._id ||
            portOperationItem?.cargoDetails?.id;

      if (!cargoId) {
        setLoader(false);
        toastSuccess("Port deleted successfully!");
        fetchPortDistance.current = true;
        let portOperation = [...currentState.calculatorData.portOperation];
        portOperation.splice(index, 1);
        portOperation = portOperation.map((item, i) => ({ ...item, index: i }));

        const updatedCalculatorData = {
          ...currentState.calculatorData,
          portOperation,
        };
        setCalculatorData(updatedCalculatorData);
        onFetchPortOperationDistance(false, updatedCalculatorData, {}, true);
        return;
      }

      deleteCargoExtraDetailVesselVesselApi({ id, cargoId })
        .then(() => {
          setLoader(false);
          toastSuccess("Port deleted successfully!");
          fetchPortDistance.current = true;
          let portOperation = [...currentState.calculatorData.portOperation];
          portOperation.splice(index, 1);
          portOperation = portOperation.map((item, i) => ({ ...item, index: i }));

          const updatedCalculatorData = {
            ...currentState.calculatorData,
            portOperation,
          };
          setCalculatorData(updatedCalculatorData);
          onFetchPortOperationDistance(false, updatedCalculatorData, {}, true);
        })
        .catch((err) => {
          setLoader(false);
          toastError(err?.data?.message || "Failed to delete port.");
        });
    },
    [id, setLoader, setCalculatorData, onFetchPortOperationDistance]
  );

  return {
    calculatorData,
    selectedVessels,
    syncBunker,
    isOverride,
    hideOrder,
    setHideOrder,
    isPublic,
    allQueryFormat,
    hideCalculator,
    setHideCalculator,
    initialFleetList,
    addFleetsArray,
    setAddFleetsArray,
    selectedSplitHire,
    setSelectedSplitHire,
    selectedSplitHireVesselOut,
    setSelectedSplitHireVesselOut,
    marginModal,
    setMarginModal,
    supplyModal,
    setSupplyModal,
    addFleetModal,
    setAddFleetModal,
    splitHireModal,
    setSplitHireModal,
    splitHireModalVesselOut,
    setSplitHireModalVesselOut,
    openAddOperation,
    setOpenAddOperation,
    openAddOperationIndex,
    setOpenAddOperationindex,
    getCurrentResult,
    fleetCheckbox,
    setFleetCheckbox,
    fetchPortDistance,
    calculationErrors,

    // Handlers
    getInitalQueryAndFleetData,
    onChangePortOperations,
    onFetchPortOperationDistance,
    onAddOperations,
    onDeleteOperations,
    sendCalculationRequest,
    onChangeFleetRowSelect,
    onClickCalculate,
    onClickCalculateAndSave,
    setCalculatorData,
    setSelectedVessels,
    setIsOverride,
    onChangeOverride,
  };
}
